import { createCatalog } from '@/lib/api/catalog/catalog-api'
import { useMemo, useState } from 'react'
import { MdAdd, MdAutoAwesome, MdDelete } from 'react-icons/md'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'

interface Axis {
    name: string
    /** Valores separados por vírgula, como o operador digita. */
    values: string
}

interface Combination {
    name: string
    attributes: { key: string; value: string }[]
}

/** Até onde o gerador vai numa leva: acima disso é engano de digitação, não catálogo. */
const MAX_COMBINATIONS = 100

const splitValues = (text: string): string[] => [
    ...new Set(
        text
            .split(',')
            .map(value => value.trim())
            .filter(Boolean)
    ),
]

/** Produto cartesiano dos eixos preenchidos, na ordem em que foram digitados. */
function buildCombinations(axes: Axis[]): Combination[] {
    const filled = axes
        .map(axis => ({ name: axis.name.trim(), values: splitValues(axis.values) }))
        .filter(axis => axis.name && axis.values.length > 0)
    if (filled.length === 0) return []

    let combos: { key: string; value: string }[][] = [[]]
    for (const axis of filled) {
        combos = combos.flatMap(combo =>
            axis.values.map(value => [...combo, { key: axis.name, value }])
        )
    }
    return combos.map(attributes => ({
        name: attributes.map(attr => attr.value).join(' / '),
        attributes,
    }))
}

/**
 * Cria as variações de um produto a partir das opções (Cor: Azul, Preto; Tamanho: P, M).
 *
 * Cada combinação vira uma variação com as opções gravadas como características — é delas
 * que a loja monta um seletor por opção. A variação nasce sem preço e sem estoque: o
 * preço se define na tela de preços das variações, e o carrinho recusa variação sem
 * preço, então nada é vendido de graça no meio do caminho.
 */
export default function VariationCombinationsModal({
    parentId,
    companyId,
    existingNames,
    onClose,
    onCreated,
}: {
    parentId: number
    companyId: number
    existingNames: string[]
    onClose: () => void
    onCreated: () => Promise<void> | void
}) {
    const [axes, setAxes] = useState<Axis[]>([
        { name: 'Cor', values: '' },
        { name: 'Tamanho', values: '' },
    ])
    const [creating, setCreating] = useState(false)

    const existing = useMemo(
        () => new Set(existingNames.map(name => name.trim().toLowerCase())),
        [existingNames]
    )
    const combinations = useMemo(() => buildCombinations(axes), [axes])
    const toCreate = combinations.filter(
        combo => !existing.has(combo.name.toLowerCase())
    )
    const tooMany = toCreate.length > MAX_COMBINATIONS

    const updateAxis = (index: number, patch: Partial<Axis>) =>
        setAxes(previous =>
            previous.map((axis, i) => (i === index ? { ...axis, ...patch } : axis))
        )

    const handleCreate = async () => {
        if (toCreate.length === 0 || tooMany) return
        setCreating(true)
        let created = 0
        const failed: string[] = []
        try {
            // Uma a uma: o nome é único dentro do produto, e um erro numa combinação não
            // deve impedir as outras.
            for (const combo of toCreate) {
                const response = await createCatalog({
                    companyId,
                    category: 'PRODUCT',
                    name: combo.name,
                    parentId,
                    price: 0,
                    sellingPrice: null,
                    productDetails: { attributes: combo.attributes },
                } as any)
                if (response.success) created++
                else failed.push(combo.name)
            }
        } finally {
            setCreating(false)
        }

        if (created > 0) {
            toast.success(
                created === 1 ? '1 variação criada' : `${created} variações criadas`
            )
        }
        if (failed.length > 0) {
            toast.error(`Não foi possível criar: ${failed.join(', ')}`)
        }
        await onCreated()
        if (failed.length === 0) onClose()
    }

    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <div className="flex items-center gap-2 mb-1">
                    <MdAutoAwesome className="text-xl text-secondary" />
                    <h3 className="text-lg font-semibold">Gerar variações</h3>
                </div>
                <p className="text-sm text-base-content/60 mb-5">
                    Informe as opções do produto e os valores de cada uma, separados por
                    vírgula. Cada combinação vira uma variação com preço, SKU e estoque
                    próprios.
                </p>

                <div className="flex flex-col gap-4">
                    {axes.map((axis, index) => (
                        <div
                            key={index}
                            className="grid grid-cols-1 sm:grid-cols-[10rem_1fr_auto] gap-3 sm:items-end"
                        >
                            <div className="form-control">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">Opção</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex.: Cor"
                                    value={axis.name}
                                    onChange={e => updateAxis(index, { name: e.target.value })}
                                />
                            </div>
                            <div className="form-control">
                                <label className="label pb-1">
                                    <span className="label-text font-medium">Valores</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex.: Azul, Preto, Branco"
                                    value={axis.values}
                                    onChange={e => updateAxis(index, { values: e.target.value })}
                                />
                            </div>
                            <button
                                type="button"
                                className="btn btn-ghost btn-square text-error"
                                title="Remover opção"
                                disabled={axes.length === 1}
                                onClick={() =>
                                    setAxes(previous => previous.filter((_, i) => i !== index))
                                }
                            >
                                <MdDelete className="text-lg" />
                            </button>
                        </div>
                    ))}
                </div>

                {axes.length < 3 && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-sm gap-1 mt-3"
                        onClick={() => setAxes(previous => [...previous, { name: '', values: '' }])}
                    >
                        <MdAdd className="text-lg" />
                        Adicionar opção
                    </button>
                )}

                <div className="mt-5 rounded-lg bg-base-200/50 p-4">
                    {combinations.length === 0 ? (
                        <p className="text-sm text-base-content/60">
                            As combinações aparecem aqui.
                        </p>
                    ) : (
                        <>
                            <p className="text-sm font-medium mb-2">
                                {toCreate.length === 0
                                    ? 'Todas as combinações já existem.'
                                    : toCreate.length === 1
                                      ? '1 variação será criada'
                                      : `${toCreate.length} variações serão criadas`}
                                {combinations.length > toCreate.length &&
                                    toCreate.length > 0 &&
                                    ` (${combinations.length - toCreate.length} já existem)`}
                            </p>
                            {tooMany ? (
                                <p className="text-sm text-error">
                                    São mais de {MAX_COMBINATIONS} combinações. Reduza os
                                    valores ou gere em partes.
                                </p>
                            ) : (
                                <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                                    {toCreate.map(combo => (
                                        <span key={combo.name} className="badge badge-outline">
                                            {combo.name}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <p className="text-xs text-base-content/50 mt-3">
                    As variações nascem sem preço e não podem ser compradas na loja até
                    terem um. Defina os preços em{' '}
                    <Link to="/registrations/catalog/variations-pricing" className="link">
                        Preços das variações
                    </Link>
                    .
                </p>

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose} disabled={creating}>
                        Cancelar
                    </button>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCreate}
                        disabled={creating || toCreate.length === 0 || tooMany}
                    >
                        {creating && <span className="loading loading-spinner loading-sm" />}
                        Criar variações
                    </button>
                </div>
            </div>
            <div className="modal-backdrop" onClick={() => !creating && onClose()} />
        </div>
    )
}
